import { CdkMenuModule } from '@angular/cdk/menu';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SearchInputComponent, trackByFactory } from '@mode/capra';
import { OrgTypes } from '@mode/shared/contract-common';
import { BehaviorSubject, Subject, debounceTime, filter, takeUntil } from 'rxjs';
import { ColorSwatchFlyoutComponent } from '../color-swatch-flyout/color-swatch-flyout.component';

export interface ColorValuesListItem {
  formattedValue: string;
  unformattedValue: string | number;
  colorHexCode: string;
  isAssigned: boolean;
}

class ListItemWrapper implements ColorValuesListItem {
  selected = false;

  constructor(private item: Readonly<ColorValuesListItem>) {}

  get formattedValue(): string {
    return this.item.formattedValue;
  }

  get unformattedValue(): string | number {
    return this.item.unformattedValue;
  }

  get colorHexCode(): string {
    return this.item.colorHexCode;
  }

  get isAssigned(): boolean {
    return this.item.isAssigned;
  }

  asColorValuesListItem() {
    return { ...this.item };
  }
}

@Component({
  standalone: true,
  selector: 'mode-color-values',
  imports: [CdkMenuModule, CommonModule, ColorSwatchFlyoutComponent, ScrollingModule, SearchInputComponent],
  templateUrl: './color-values.component.html',
  styleUrls: ['./color-values.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorValuesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() listOfItems: Array<ColorValuesListItem> = [];
  @Input() selectedColorPalette!: OrgTypes.ColorPalette;
  @Input() allColorPalettes: OrgTypes.ColorPalette[] = [];

  @Output() colorChanged = new EventEmitter<ColorValuesListItem>();
  @Output() unassignColors = new EventEmitter<ColorValuesListItem[]>();
  @Input() vizToken = '';

  searchTerm = '';
  searchTerm$ = new BehaviorSubject('');
  filteredListOfItems: Array<ListItemWrapper> = [];
  visibleListOfItems$ = new BehaviorSubject<Array<ListItemWrapper>>([]);

  private lastSelected: ListItemWrapper | undefined;
  private destroyed$ = new Subject<void>();

  trackByValue = trackByFactory<ColorValuesListItem>(({ unformattedValue }) => unformattedValue);

  ngOnInit(): void {
    this.searchTerm$
      .pipe(
        takeUntil(this.destroyed$),
        filter((searchTerm) => searchTerm != null),
        debounceTime(400)
      )
      .subscribe((searchTerm: string) => {
        this.searchTerm = searchTerm;
        const lowercasedTerm = searchTerm.toLowerCase();
        this.filteredListOfItems = this.listOfItems
          .filter((item) => item.formattedValue.toLowerCase().includes(lowercasedTerm))
          .map((listItem) => {
            return new ListItemWrapper(listItem);
          });
        this.visibleListOfItems$.next(this.filteredListOfItems);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.listOfItems) {
      const lowercasedTerm = this.searchTerm.toLowerCase();
      this.filteredListOfItems = this.listOfItems
        .filter((item) => item.formattedValue.toLowerCase().includes(lowercasedTerm))
        .map((listItem) => {
          return new ListItemWrapper(listItem);
        });
      this.visibleListOfItems$.next(this.filteredListOfItems);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSearchTermChanged(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  onColorChanged(event: { colorSelected: string; selectedPalette: OrgTypes.ColorPalette }, unformattedValue: string) {
    const item = this.listOfItems.find((item) => item.unformattedValue === unformattedValue);
    if (item && item.colorHexCode.toLowerCase() !== event.colorSelected?.toLowerCase()) {
      item.colorHexCode = event.colorSelected;
      this.colorChanged.emit(item);
    }
    this.selectedColorPalette = event.selectedPalette;
  }

  onItemClick(item: ListItemWrapper, event: PointerEvent) {
    if (!item.isAssigned) {
      return;
    }

    const selected = item.selected;
    if (!event.shiftKey && !event.metaKey && !event.ctrlKey) {
      // Deselect everything
      this.filteredListOfItems.forEach((listItem) => (listItem.selected = false));
      item.selected = !selected;
      this.lastSelected = item.selected ? item : undefined;
    } else if (event.shiftKey) {
      // Select/deselect everything between this and the last
      if (this.lastSelected === undefined) {
        // Shift click never deselects. Select if selectable (ie has a color assigned)
        item.selected = true;
        this.lastSelected = item;
      } else {
        const lastIndex = this.filteredListOfItems.findIndex((listItem) => listItem === this.lastSelected);
        const clickIndex = this.filteredListOfItems.findIndex((listItem) => listItem === item);
        const start = Math.min(lastIndex, clickIndex);
        const end = Math.max(lastIndex, clickIndex) + 1;
        const elements = this.filteredListOfItems.slice(start, end);
        elements.forEach((listItem) => {
          listItem.selected = listItem.isAssigned && !selected;
        });
        item.selected = true;
        this.lastSelected = item;
      }
    } else if (event.metaKey || event.ctrlKey) {
      // Don't clear anything, just toggle the current item.
      item.selected = !selected;
      this.lastSelected = item.selected ? item : undefined;
    }
  }

  onContextMenuClick(item: ListItemWrapper, event: MouseEvent) {
    if (!item.isAssigned || !item.selected) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  unassignSelected() {
    const unassigned = this.filteredListOfItems
      .filter((item) => item.selected)
      .map((item) => {
        return item.asColorValuesListItem();
      });
    this.unassignColors.emit(unassigned);
  }
}
